import { useEffect, useMemo, useState } from "react";
import {
  CheckSquare,
  Database,
  Edit2,
  FileText,
  Layers,
  Loader2,
  Trash2,
  Check,
  Video,
  Music,
} from "lucide-react";
import { useOutletContext } from "react-router-dom";
import {
  EmptyState,
  SearchBar,
} from "../../components/adminDashboard/index.jsx";
import Pagination from "../../components/common/Pagination.jsx";
import { workspaceService, userUseCaseService } from "../../services/index.js";
import { appendFrontendApiLog } from "../../utils/frontendApiLogger.js";

const readArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];

  const directCandidates = [
    value.data,
    value.result,
    value.results,
    value.items,
    value.list,
    value.records,
  ];
  for (const candidate of directCandidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  // Handle nested API envelopes like { data: { data: [...] } }.
  for (const candidate of directCandidates) {
    if (candidate && typeof candidate === "object") {
      const nested = readArray(candidate);
      if (nested.length > 0) return nested;
    }
  }

  // Last fallback: search shallow object values for the first array.
  for (const val of Object.values(value)) {
    if (Array.isArray(val)) return val;
  }

  return [];
};

const pick = (obj = {}, keys = []) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
    const fallbackKey = Object.keys(obj || {}).find(
      (k) => k.toLowerCase() === String(key).toLowerCase(),
    );
    if (
      fallbackKey &&
      obj[fallbackKey] !== undefined &&
      obj[fallbackKey] !== null
    ) {
      return obj[fallbackKey];
    }
  }
  return undefined;
};

const pickWithPresence = (obj = {}, keys = []) => {
  if (!obj || typeof obj !== "object") {
    return { found: false, value: undefined };
  }
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return { found: true, value: obj[key] };
    }
    const fallbackKey = Object.keys(obj || {}).find(
      (k) => k.toLowerCase() === String(key).toLowerCase(),
    );
    if (fallbackKey && Object.prototype.hasOwnProperty.call(obj, fallbackKey)) {
      return { found: true, value: obj[fallbackKey] };
    }
  }
  return { found: false, value: undefined };
};

const toServiceType = (value = "") => {
  const normalized = String(value).toLowerCase();
  if (normalized.includes("sql")) return "SqlAnalytics";
  if (normalized.includes("file")) return "FileAnalytics";
  if (normalized.includes("media")) return "MediaAnalytics";
  return "";
};

const parseCsv = (value = "") =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeTableName = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[[\]`"]/g, "")
    .replace(/\s+/g, "");

const isSameTableName = (left = "", right = "") => {
  const l = normalizeTableName(left);
  const r = normalizeTableName(right);
  if (!l || !r) return false;
  if (l === r) return true;
  const lLast = l.split(".").pop();
  const rLast = r.split(".").pop();
  return lLast === rLast || l.endsWith(`.${rLast}`) || r.endsWith(`.${lLast}`);
};

const getCompanyIdFromUser = (user = {}) =>
  String(pick(user, ["companyId", "companyID", "companyid"]) || "");

const getUserId = (user = {}) =>
  Number(pick(user, ["id", "userId", "userID", "userid"]) || 0);

const parseWorkspaceDetails = (payload = {}) => {
  const parseArrayLike = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    return null;
  };

  const findDetails = (node, depth = 0) => {
    if (!node || depth > 5) return null;
    const direct = parseArrayLike(
      pick(node, [
        "useCaseDetails",
        "usecaseDetails",
        "useCaseDetail",
        "usecaseDetail",
      ]),
    );
    if (direct) return direct;

    if (Array.isArray(node)) {
      for (const item of node) {
        const found = findDetails(item, depth + 1);
        if (found) return found;
      }
      return null;
    }
    if (typeof node === "object") {
      for (const val of Object.values(node)) {
        const found = findDetails(val, depth + 1);
        if (found) return found;
      }
    }
    return null;
  };

  const arr = findDetails(payload) || [];
  return arr.flatMap((detail) => {
    const selectedService = toServiceType(
      pick(detail, ["selectedService", "serviceType", "service"]) || "",
    );
    const selectedFieldsRaw =
      pick(detail, ["selectedFields", "selectedColumn", "selectedColumns"]) ||
      "";
    let tableOrFileName = String(
      pick(detail, ["tableOrFileName", "tableName", "fileName"]) || "",
    ).trim();
    if (!tableOrFileName && selectedService === "FileAnalytics") {
      const rawPath = String(selectedFieldsRaw || "");
      const parts = rawPath.split(/[\\/]/);
      tableOrFileName = parts[parts.length - 1] || "";
    }
    const normalized = {
      useCaseDetailId: Number(
        pick(detail, ["useCaseDetailId", "usecaseDetailId"]) || 0,
      ),
      selectedService,
      tableOrFileName,
      selectedFieldsRaw,
      selectedFieldsList: Array.isArray(selectedFieldsRaw)
        ? selectedFieldsRaw.map((x) => String(x).trim()).filter(Boolean)
        : parseCsv(selectedFieldsRaw),
    };
    if (selectedService === "SqlAnalytics" && tableOrFileName.includes(",")) {
      return parseCsv(tableOrFileName).map((tableName) => ({
        ...normalized,
        tableOrFileName: tableName,
      }));
    }
    return [normalized];
  });
};

const findTableDetail = (details = [], tableName = "") =>
  details.find(
    (detail) =>
      detail.selectedService === "SqlAnalytics" &&
      isSameTableName(detail.tableOrFileName, tableName),
  );

const countSqlTables = (details = []) =>
  new Set(
    details
      .filter((d) => d.selectedService === "SqlAnalytics" && d.tableOrFileName)
      .map((d) => normalizeTableName(d.tableOrFileName))
      .filter(Boolean),
  ).size;

const countPdfFiles = (details = []) =>
  new Set(
    details
      .filter((d) => d.selectedService === "FileAnalytics" && d.tableOrFileName)
      .map((d) => String(d.tableOrFileName).trim().toLowerCase())
      .filter(Boolean),
  ).size;

const countMediaFiles = (details = []) =>
  new Set(
    details
      .filter((d) => d.selectedService === "MediaAnalytics" && d.tableOrFileName)
      .map((d) => String(d.tableOrFileName).trim().toLowerCase())
      .filter(Boolean),
  ).size;

const getWorkspaceDetailsBundle = (workspacePayload = {}) => {
  const byIdDetails = parseWorkspaceDetails(workspacePayload?.byId || {});
  const availableDetails =
    byIdDetails.length > 0
      ? byIdDetails
      : parseWorkspaceDetails(workspacePayload?.base || workspacePayload || {});
  const workspaceTableDetails = availableDetails.filter(
    (detail) =>
      detail.selectedService === "SqlAnalytics" && detail.tableOrFileName,
  );
  const workspaceFileDetails = availableDetails.filter(
    (detail) =>
      detail.selectedService === "FileAnalytics" && detail.tableOrFileName,
  );
  const workspaceMediaDetails = availableDetails.filter(
    (detail) =>
      detail.selectedService === "MediaAnalytics" && detail.tableOrFileName,
  );
  return { workspaceTableDetails, workspaceFileDetails, workspaceMediaDetails };
};

const toInitialSelection = (
  workspaceTableDetails = [],
  workspaceFileDetails = [],
  workspaceMediaDetails = [],
) => {
  const selectedColumnsByTable = {};
  workspaceTableDetails.forEach((detail) => {
    selectedColumnsByTable[detail.tableOrFileName] = detail.selectedFieldsList;
  });
  return {
    selectedTableNames: [
      ...new Set(workspaceTableDetails.map((d) => d.tableOrFileName)),
    ],
    selectedColumnsByTable,
    selectedFileNames: [
      ...new Set(workspaceFileDetails.map((d) => d.tableOrFileName)),
    ],
    selectedMediaNames: [
      ...new Set(workspaceMediaDetails.map((d) => d.tableOrFileName)),
    ],
  };
};

const createEmptySelection = () => ({
  selectedTableNames: [],
  selectedColumnsByTable: {},
  selectedFileNames: [],
  selectedMediaNames: [],
});

export default function WorkspaceMapping() {
  const {
    companiesData,
    usersData,
    workspacesData,
    isWorkspaceMappingOnboarding,
    setIsWorkspaceMappingOnboarding,
    selectedWorkspaceMapping,
    setSelectedWorkspaceMapping,
  } = useOutletContext();

  const [refreshKey, setRefreshKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoadingRows, setIsLoadingRows] = useState(false);
  const [tableError, setTableError] = useState("");

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [row.companyName, row.workspaceName, row.userName, row.userEmail]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [rows, searchQuery]);

  const totalPages = Math.ceil(filteredRows.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRows = filteredRows.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, itemsPerPage]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const fetchRows = async () => {
    setIsLoadingRows(true);
    setTableError("");
    try {
      const companyMap = new Map(
        (companiesData || []).map((c) => [String(c.companyID), c.companyName]),
      );
      const workspaceMap = new Map(
        (workspacesData || []).map((w) => [String(w.useCaseId), w.useCaseName]),
      );
      const requests = (usersData || [])
        .map((user) => ({
          userId: getUserId(user),
          companyId: getCompanyIdFromUser(user),
          user,
        }))
        .filter((entry) => entry.userId > 0 && entry.companyId);

      const responses = await Promise.all(
        requests.map(async (entry) => {
          try {
            const response = await userUseCaseService.getUseCaseListForUser({
              companyID: Number(entry.companyId),
              userID: Number(entry.userId),
            });
            return { entry, mappings: readArray(response) };
          } catch {
            return { entry, mappings: [] };
          }
        }),
      );

      const nextRows = [];
      responses.forEach(({ entry, mappings }) => {
        mappings.forEach((mapping) => {
          const userUseCaseId = Number(
            pick(mapping, ["userUseCaseId", "userUseCaseID"]) || 0,
          );
          const useCaseId = Number(
            pick(mapping, ["useCaseId", "useCaseID", "usecaseId"]) || 0,
          );
          const details = parseWorkspaceDetails(mapping);
          const sqlCountFromDetails = countSqlTables(details);
          const fileCountFromDetails = countPdfFiles(details);
          const mediaCountFromDetails = countMediaFiles(details);
          const sqlConfiguredPick = pickWithPresence(mapping, [
            "sqlConfigured",
            "sqlCount",
            "tableCount",
          ]);
          const fileConfiguredPick = pickWithPresence(mapping, [
            "fileConfigured",
            "pdfConfigured",
            "fileCount",
            "pdfCount",
          ]);
          const mediaConfiguredPick = pickWithPresence(mapping, [
            "mediaConfigured",
            "mediaCount",
          ]);
          const sqlCountFromMapping = Number(sqlConfiguredPick.value);
          const fileCountFromMapping = Number(fileConfiguredPick.value);
          const mediaCountFromMapping = Number(mediaConfiguredPick.value);
          const hasSqlCountFromMapping =
            sqlConfiguredPick.found && Number.isFinite(sqlCountFromMapping);
          const hasFileCountFromMapping =
            fileConfiguredPick.found && Number.isFinite(fileCountFromMapping);
          const hasMediaCountFromMapping =
            mediaConfiguredPick.found && Number.isFinite(mediaCountFromMapping);
          const sqlCount =
            sqlCountFromDetails > 0
              ? sqlCountFromDetails
              : hasSqlCountFromMapping
                ? sqlCountFromMapping
                : 0;
          const fileCount =
            fileCountFromDetails > 0
              ? fileCountFromDetails
              : hasFileCountFromMapping
                ? fileCountFromMapping
                : 0;
          const mediaCount =
            mediaCountFromDetails > 0
              ? mediaCountFromDetails
              : hasMediaCountFromMapping
                ? mediaCountFromMapping
                : 0;
          nextRows.push({
            userUseCaseId,
            useCaseId,
            companyId: String(
              pick(mapping, ["companyId", "companyID"]) || entry.companyId,
            ),
            companyName:
              companyMap.get(
                String(
                  pick(mapping, ["companyId", "companyID"]) || entry.companyId,
                ),
              ) || entry.user.companyName,
            workspaceName:
              workspaceMap.get(String(useCaseId)) ||
              pick(mapping, ["useCaseName"]) ||
              "Unknown",
            userId: entry.userId,
            userName:
              `${entry.user.firstName || ""} ${entry.user.lastName || ""}`.trim(),
            userEmail: entry.user.email || "",
            sqlCount,
            fileCount,
            mediaCount,
            needsDetailCountFetch:
              userUseCaseId > 0 &&
              details.length === 0 &&
              !hasSqlCountFromMapping &&
              !hasFileCountFromMapping &&
              !hasMediaCountFromMapping,
            raw: mapping,
          });
        });
      });
      const rowsNeedingCounts = nextRows.filter(
        (row) => row.needsDetailCountFetch,
      );
      if (rowsNeedingCounts.length > 0) {
        const countPairs = await Promise.all(
          rowsNeedingCounts.map(async (row) => {
            try {
              const detailRes = await userUseCaseService.getById({
                userUseCaseID: Number(row.userUseCaseId),
              });
              const detailPayload = detailRes?.data || detailRes || {};
              const detailed = parseWorkspaceDetails(detailPayload);
              return [
                row.userUseCaseId,
                {
                  sqlCount: countSqlTables(detailed),
                  fileCount: countPdfFiles(detailed),
                  mediaCount: countMediaFiles(detailed),
                },
              ];
            } catch {
              return null;
            }
          }),
        );
        const countMap = new Map(countPairs.filter(Boolean));
        nextRows.forEach((row) => {
          if (!row.needsDetailCountFetch) return;
          const fetched = countMap.get(row.userUseCaseId);
          if (!fetched) return;
          row.sqlCount = fetched.sqlCount;
          row.fileCount = fetched.fileCount;
          row.mediaCount = fetched.mediaCount;
        });
      }

      setRows(
        nextRows
          .map(({ needsDetailCountFetch, ...row }) => row)
          .filter((row, index, arr) => {
            const rowKey =
              row.userUseCaseId > 0
                ? `id:${row.userUseCaseId}`
                : `fallback:${row.companyId}-${row.userId}-${row.useCaseId}`;
            return (
              arr.findIndex((x) => {
                const xKey =
                  x.userUseCaseId > 0
                    ? `id:${x.userUseCaseId}`
                    : `fallback:${x.companyId}-${x.userId}-${x.useCaseId}`;
                return xKey === rowKey;
              }) === index
            );
          }),
      );
    } catch (error) {
      setTableError(error?.message || "Failed to load mappings.");
    } finally {
      setIsLoadingRows(false);
    }
  };

  useEffect(() => {
    if (!isWorkspaceMappingOnboarding) fetchRows();
  }, [refreshKey, usersData, workspacesData, isWorkspaceMappingOnboarding]);

  const handleEdit = async (row) => {
    try {
      const detailRes = await userUseCaseService.getById({
        userUseCaseID: Number(row.userUseCaseId),
      });
      setSelectedWorkspaceMapping({
        ...row,
        detailPayload: detailRes?.data || detailRes || row.raw,
      });
    } catch {
      setSelectedWorkspaceMapping({ ...row, detailPayload: row.raw });
    }
    setIsWorkspaceMappingOnboarding(true);
  };

  const handleDelete = async (row) => {
    const ok = window.confirm(
      `Delete mapping for ${row.userName} in workspace "${row.workspaceName}"?`,
    );
    if (!ok) return;
    try {
      await userUseCaseService.delete({
        userUseCaseId: row.userUseCaseId,
        updatedBy: Number(localStorage.getItem("userId") || 0),
      });
      fetchRows();
      showToast("Mapping deleted successfully", "success");
    } catch (error) {
      showToast(error?.message || "Failed to delete mapping.", "error");
    }
  };

  const handleSaved = () => {
    setRefreshKey((prev) => prev + 1);
    setSelectedWorkspaceMapping(null);
    setIsWorkspaceMappingOnboarding(false);
  };

  const handleCancel = () => {
    setSelectedWorkspaceMapping(null);
    setIsWorkspaceMappingOnboarding(false);
  };

  if (!isWorkspaceMappingOnboarding) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 flex flex-col h-auto max-h-[calc(100vh-140px)]">
        <div className="p-5 border-b border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white ml-2">
            Workspace Mappings
          </h3>
          <div className="flex items-center gap-3">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
            />
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search company, workspace, or user..."
            />
          </div>
        </div>
        {tableError && (
          <div className="mx-5 mt-4 text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 rounded-xl">
            {tableError}
          </div>
        )}
        <div className="w-full flex-1 overflow-auto hide-scrollbar">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-slate-50/50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-sm">
                <th className="px-6 py-3 font-semibold whitespace-nowrap">Company</th>
                <th className="px-6 py-3 font-semibold whitespace-nowrap">Workspace</th>
                <th className="px-6 py-3 font-semibold whitespace-nowrap">User</th>
                <th className="px-6 py-3 font-semibold whitespace-nowrap">Permissions</th>
                <th className="px-6 py-3 font-semibold text-right whitespace-nowrap">Actions</th>
               </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {!isLoadingRows && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={5}>
                    <EmptyState
                      query={searchQuery}
                      label="workspace mappings"
                    />
                  </td>
                </tr>
              )}
              {isLoadingRows && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-10 text-center text-slate-500 dark:text-slate-400"
                  >
                    Loading mappings...
                  </td>
                </tr>
              )}
              {paginatedRows.map((row) => (
                <tr
                  key={row.userUseCaseId}
                  className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30"
                >
                  <td className="px-6 py-3 text-sm font-medium text-slate-700 dark:text-slate-300">
                    {row.companyName}
                  </td>
                  <td className="px-6 py-3 text-sm text-slate-700 dark:text-slate-300">
                    {row.workspaceName}
                  </td>
                  <td className="px-6 py-3">
                    <div className="text-sm text-slate-700 dark:text-slate-300">
                      {row.userName || "N/A"}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {row.userEmail}
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100/80 dark:bg-slate-700/25 px-2 py-1 rounded border border-slate-200 dark:border-slate-600/40">
                        <Database size={12} className="text-amber-500" />{" "}
                        {row.sqlCount} Tables
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100/80 dark:bg-slate-700/25 px-2 py-1 rounded border border-slate-200 dark:border-slate-600/40">
                        <FileText size={12} className="text-[#36a852]" />{" "}
                        {row.fileCount} Files
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100/80 dark:bg-slate-700/25 px-2 py-1 rounded border border-slate-200 dark:border-slate-600/40">
                        <Video size={12} className="text-purple-500" />{" "}
                        {row.mediaCount} Media
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center justify-end">
                      <div className="flex items-center gap-1 bg-slate-100/80 dark:bg-slate-700/25 rounded-xl p-1">
                        <button
                          onClick={() => handleEdit(row)}
                          className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(row)}
                          className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <MappingEditor
      companiesData={companiesData}
      usersData={usersData}
      selectedWorkspaceMapping={selectedWorkspaceMapping}
      onSaved={handleSaved}
      onCancel={handleCancel}
    />
  );
}

function MappingEditor({
  companiesData,
  usersData,
  selectedWorkspaceMapping,
  onSaved,
  onCancel,
}) {
  const isEditMode = Boolean(selectedWorkspaceMapping?.userUseCaseId);
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    String(selectedWorkspaceMapping?.companyId || ""),
  );
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceSearch, setWorkspaceSearch] = useState("");
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState(
    isEditMode && selectedWorkspaceMapping?.useCaseId
      ? [String(selectedWorkspaceMapping.useCaseId)]
      : [],
  );
  const [workspaceDetailsById, setWorkspaceDetailsById] = useState({});
  const [workspaceSelectionsById, setWorkspaceSelectionsById] = useState({});
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(
    String(selectedWorkspaceMapping?.useCaseId || ""),
  );
  const [selectedWorkspaceData, setSelectedWorkspaceData] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState(
    selectedWorkspaceMapping?.userId ? [selectedWorkspaceMapping.userId] : [],
  );
  const [userSearch, setUserSearch] = useState("");
  const [selectedTableNames, setSelectedTableNames] = useState([]);
  const [selectedColumnsByTable, setSelectedColumnsByTable] = useState({});
  const [selectedFileNames, setSelectedFileNames] = useState([]);
  const [selectedMediaNames, setSelectedMediaNames] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const [toast, setToast] = useState({
    visible: false,
    message: "",
    type: "success",
  });
  const showToast = (message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(
      () => setToast({ visible: false, message: "", type: "success" }),
      2000,
    );
  };

  const availableDetails = useMemo(() => {
    const byIdDetails = parseWorkspaceDetails(
      selectedWorkspaceData?.byId || {},
    );
    if (byIdDetails.length > 0) return byIdDetails;
    const baseDetails = parseWorkspaceDetails(
      selectedWorkspaceData?.base || {},
    );
    if (baseDetails.length > 0) return baseDetails;
    return parseWorkspaceDetails(selectedWorkspaceData || {});
  }, [selectedWorkspaceData]);
  const mappedDetails = useMemo(
    () => parseWorkspaceDetails(selectedWorkspaceMapping?.detailPayload || {}),
    [selectedWorkspaceMapping],
  );
  const workspaceTableDetails = useMemo(
    () =>
      availableDetails.filter(
        (detail) =>
          detail.selectedService === "SqlAnalytics" && detail.tableOrFileName,
      ),
    [availableDetails],
  );
  const workspaceFileDetails = useMemo(
    () =>
      availableDetails.filter(
        (detail) =>
          detail.selectedService === "FileAnalytics" && detail.tableOrFileName,
      ),
    [availableDetails],
  );
  const workspaceMediaDetails = useMemo(
    () =>
      availableDetails.filter(
        (detail) =>
          detail.selectedService === "MediaAnalytics" && detail.tableOrFileName,
      ),
    [availableDetails],
  );
  const companyUsers = useMemo(() => {
    if (!selectedCompanyId) return [];
    return (usersData || []).filter(
      (user) => getCompanyIdFromUser(user) === String(selectedCompanyId),
    );
  }, [usersData, selectedCompanyId]);
  const filteredCompanyUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return companyUsers;
    return companyUsers.filter((user) => {
      const fullName =
        `${user.firstName || ""} ${user.middleName || ""} ${user.lastName || ""}`
          .trim()
          .toLowerCase();
      return (
        fullName.includes(q) ||
        String(user.email || "")
          .toLowerCase()
          .includes(q) ||
        String(user.mobileNo || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [companyUsers, userSearch]);
  const filteredWorkspaces = useMemo(() => {
    const q = workspaceSearch.trim().toLowerCase();
    if (!q) return workspaces;
    return workspaces.filter((workspace) =>
      `${workspace.useCaseName || ""} ${workspace.companyName || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [workspaces, workspaceSearch]);

  useEffect(() => {
    if (!selectedCompanyId) {
      setWorkspaces([]);
      setSelectedWorkspaceId("");
      setSelectedWorkspaceData(null);
      setSelectedWorkspaceIds([]);
      setWorkspaceDetailsById({});
      setWorkspaceSelectionsById({});
      if (!isEditMode) {
        setSelectedUsers([]);
      }
      return;
    }
    const companyIdNum = Number(selectedCompanyId);
    setWorkspaces([]);
    if (!isEditMode) {
      setSelectedWorkspaceId("");
      setSelectedWorkspaceData(null);
      setSelectedWorkspaceIds([]);
      setWorkspaceDetailsById({});
      setWorkspaceSelectionsById({});
      setSelectedUsers([]);
    }
    let active = true;
    const load = async () => {
      const requestPayload = { companyID: companyIdNum };
      appendFrontendApiLog("workspaceMapping.companyWorkspaceFetch.request", {
        request: requestPayload,
      });

      const response =
        await workspaceService.getUseCaseListForCompany(companyIdNum);
      appendFrontendApiLog("workspaceMapping.companyWorkspaceFetch.response", {
        request: requestPayload,
        response,
      });

      if (!active) return;
      const parsedWorkspaces = readArray(response);
      appendFrontendApiLog("workspaceMapping.companyWorkspaceFetch.parsed", {
        request: requestPayload,
        parsedCount: parsedWorkspaces.length,
        parsedWorkspaceIds: parsedWorkspaces.map((w) =>
          String(pick(w, ["useCaseId", "useCaseID", "usecaseId"]) || ""),
        ),
        parsedWorkspaceNames: parsedWorkspaces.map((w) =>
          String(pick(w, ["useCaseName", "usecaseName"]) || ""),
        ),
      });
      setWorkspaces(parsedWorkspaces);
    };
    load().catch((loadError) => {
      appendFrontendApiLog("workspaceMapping.companyWorkspaceFetch.error", {
        request: { companyID: companyIdNum },
        error: loadError?.message || String(loadError),
      });
      if (!active) return;
      setWorkspaces([]);
    });
    return () => {
      active = false;
    };
  }, [selectedCompanyId, isEditMode]);

  useEffect(() => {
    if (isEditMode) {
      if (!selectedWorkspaceId) return;
      const existsInCompany = workspaces.some(
        (workspace) =>
          String(workspace.useCaseId) === String(selectedWorkspaceId),
      );
      if (!existsInCompany) {
        setSelectedWorkspaceId("");
        setSelectedWorkspaceData(null);
      }
      return;
    }
    setSelectedWorkspaceIds((prev) =>
      prev.filter((workspaceId) =>
        workspaces.some(
          (workspace) => String(workspace.useCaseId) === String(workspaceId),
        ),
      ),
    );
  }, [workspaces, selectedWorkspaceId, isEditMode]);

  useEffect(() => {
    if (isEditMode || !selectedCompanyId || selectedWorkspaceIds.length === 0)
      return;
    let active = true;
    const load = async () => {
      const missingWorkspaceIds = selectedWorkspaceIds.filter(
        (workspaceId) => !workspaceDetailsById[workspaceId],
      );
      if (missingWorkspaceIds.length === 0) return;

      for (const workspaceId of missingWorkspaceIds) {
        try {
          const [byIdResponse] = await Promise.all([
            workspaceService.getById(Number(workspaceId)).catch(() => null),
          ]);
          if (!active) return;
          const payload = {
            base: null,
            byId: byIdResponse?.data || byIdResponse || null,
          };
          const { workspaceTableDetails, workspaceFileDetails, workspaceMediaDetails } =
            getWorkspaceDetailsBundle(payload);

          setWorkspaceDetailsById((prev) => ({
            ...prev,
            [workspaceId]: payload,
          }));
          setWorkspaceSelectionsById((prev) =>
            prev[workspaceId]
              ? prev
              : {
                  ...prev,
                  [workspaceId]: toInitialSelection(
                    workspaceTableDetails,
                    workspaceFileDetails,
                    workspaceMediaDetails,
                  ),
                },
          );
        } catch {
          if (!active) return;
          setWorkspaceDetailsById((prev) => ({
            ...prev,
            [workspaceId]: { base: null, byId: null, error: true },
          }));
        }
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [
    isEditMode,
    selectedCompanyId,
    selectedWorkspaceIds,
    workspaceDetailsById,
  ]);

  useEffect(() => {
    if (!selectedCompanyId || !selectedWorkspaceId) {
      setSelectedWorkspaceData(null);
      return;
    }
    const load = async () => {
      let byId = null;
      try {
        const byIdResponse = await workspaceService.getById(
          Number(selectedWorkspaceId),
        );
        byId = byIdResponse?.data || byIdResponse || null;
      } catch {
        byId = null;
      }
      setSelectedWorkspaceData({ base: null, byId });
    };
    load();
  }, [selectedCompanyId, selectedWorkspaceId]);

  useEffect(() => {
    const base =
      isEditMode && mappedDetails.length > 0 ? mappedDetails : availableDetails;
    const tableDetails = base.filter(
      (detail) => detail.selectedService === "SqlAnalytics",
    );
    const fileDetails = base.filter(
      (detail) => detail.selectedService === "FileAnalytics",
    );
    const mediaDetails = base.filter(
      (detail) => detail.selectedService === "MediaAnalytics",
    );
    const cols = {};
    tableDetails.forEach((detail) => {
      cols[detail.tableOrFileName] = detail.selectedFieldsList;
    });
    setSelectedTableNames(tableDetails.map((detail) => detail.tableOrFileName));
    setSelectedFileNames(fileDetails.map((detail) => detail.tableOrFileName));
    setSelectedMediaNames(mediaDetails.map((detail) => detail.tableOrFileName));
    setSelectedColumnsByTable(cols);
  }, [availableDetails, mappedDetails, isEditMode]);

  const handleSave = async () => {
    if (!selectedCompanyId) {
      showToast("Please select a Company", "error");
      return;
    }
    if (selectedUsers.length === 0) {
      showToast("Please select at least one User", "error");
      return;
    }
    if (isEditMode && !selectedWorkspaceId) {
      showToast("Please select a Workspace", "error");
      return;
    }
    if (!isEditMode && selectedWorkspaceIds.length === 0) {
      showToast("Please select at least one Workspace", "error");
      return;
    }

    // ✅ Validate at least 1 table, file, or media selected
    if (isEditMode) {
      if (selectedTableNames.length === 0 && selectedFileNames.length === 0 && selectedMediaNames.length === 0) {
        showToast("Please select at least one SQL Table, PDF File, or Media File", "error");
        return;
      }
    } else {
      const hasAnySelection = selectedWorkspaceIds.some((workspaceId) => {
        const selection =
          workspaceSelectionsById[workspaceId] || createEmptySelection();
        return (
          (selection.selectedTableNames || []).length > 0 ||
          (selection.selectedFileNames || []).length > 0 ||
          (selection.selectedMediaNames || []).length > 0
        );
      });
      if (!hasAnySelection) {
        showToast(
          "Please select at least one SQL Table, PDF File, or Media File in a Workspace",
          "error",
        );
        return;
      }
    }

    setIsSaving(true);
    try {
      if (isEditMode) {
        const sqlDetails = selectedTableNames.map((tableName) => {
          const source = findTableDetail(workspaceTableDetails, tableName);
          return {
            useCaseDetailId: Number(source?.useCaseDetailId || 0),
            selectedService: "SqlAnalytics",
            tableOrFileName: source?.tableOrFileName || tableName,
            selectedFields: (selectedColumnsByTable[tableName] || []).join(","),
          };
        });
        const fileDetails = selectedFileNames.map((fileName) => {
          const source = workspaceFileDetails.find(
            (detail) => detail.tableOrFileName === fileName,
          );
          return {
            useCaseDetailId: Number(source?.useCaseDetailId || 0),
            selectedService: "FileAnalytics",
            tableOrFileName: source?.tableOrFileName || fileName,
            selectedFields: String(source?.selectedFieldsRaw || ""),
          };
        });
        const mediaDetails = selectedMediaNames.map((mediaName) => {
          const source = workspaceMediaDetails.find(
            (detail) => detail.tableOrFileName === mediaName,
          );
          return {
            useCaseDetailId: Number(source?.useCaseDetailId || 0),
            selectedService: "MediaAnalytics",
            tableOrFileName: source?.tableOrFileName || mediaName,
            selectedFields: String(source?.selectedFieldsRaw || ""),
          };
        });
        const useCaseDetails = [...sqlDetails, ...fileDetails, ...mediaDetails];
        await userUseCaseService.update({
          userUseCaseId: Number(selectedWorkspaceMapping.userUseCaseId),
          companyId: Number(selectedCompanyId),
          userId: Number(selectedUsers[0]),
          useCaseDetails,
        });
      } else {
        for (const workspaceId of selectedWorkspaceIds) {
          const payload = workspaceDetailsById[workspaceId] || {};
          const {
            workspaceTableDetails: tableDetails,
            workspaceFileDetails: fileDetails,
            workspaceMediaDetails: mediaDetails,
          } = getWorkspaceDetailsBundle(payload);
          const selection =
            workspaceSelectionsById[workspaceId] || createEmptySelection();
          const sqlDetails = selection.selectedTableNames.map((tableName) => {
            const source = findTableDetail(tableDetails, tableName);
            return {
              useCaseDetailId: Number(source?.useCaseDetailId || 0),
              selectedService: "SqlAnalytics",
              tableOrFileName: source?.tableOrFileName || tableName,
              selectedFields: (
                selection.selectedColumnsByTable?.[tableName] || []
              ).join(","),
            };
          });
          const selectedFiles = selection.selectedFileNames || [];
          const filteredFileDetails = selectedFiles.map((fileName) => {
            const source = fileDetails.find(
              (detail) => detail.tableOrFileName === fileName,
            );
            return {
              useCaseDetailId: Number(source?.useCaseDetailId || 0),
              selectedService: "FileAnalytics",
              tableOrFileName: source?.tableOrFileName || fileName,
              selectedFields: String(source?.selectedFieldsRaw || ""),
            };
          });
          const selectedMedias = selection.selectedMediaNames || [];
          const filteredMediaDetails = selectedMedias.map((mediaName) => {
            const source = mediaDetails.find(
              (detail) => detail.tableOrFileName === mediaName,
            );
            return {
              useCaseDetailId: Number(source?.useCaseDetailId || 0),
              selectedService: "MediaAnalytics",
              tableOrFileName: source?.tableOrFileName || mediaName,
              selectedFields: String(source?.selectedFieldsRaw || ""),
            };
          });
          const useCaseDetails = [...sqlDetails, ...filteredFileDetails, ...filteredMediaDetails];

          for (const userId of selectedUsers) {
            await userUseCaseService.insert({
              companyId: Number(selectedCompanyId),
              userId: Number(userId),
              useCaseId: Number(workspaceId),
              useCaseDetails,
            });
          }
        }
      }
      showToast(
        isEditMode
          ? "Mapping updated successfully"
          : "Mapping created successfully",
        "success",
      );
      setTimeout(() => onSaved(), 1600);
    } catch (saveError) {
      showToast(saveError?.message || "Failed to save mapping.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 flex flex-col h-auto max-h-[calc(100vh-140px)]">
      <div className="p-6 border-b border-slate-100 dark:border-slate-700">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
          {isEditMode ? "Edit Workspace Mapping" : "Create Workspace Mapping"}
        </h3>
      </div>
      <div className="p-6 space-y-6 overflow-auto hide-scrollbar">
        <div
          className={`grid grid-cols-1 ${isEditMode ? "md:grid-cols-2" : ""} gap-4`}
        >
          <select
            disabled={isEditMode}
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-900/50 text-slate-900 dark:text-white text-sm disabled:opacity-70"
          >
            <option value="">Select company *</option>
            {(companiesData || []).map((company) => (
              <option
                key={String(company.companyID)}
                value={String(company.companyID)}
              >
                {company.companyName}
              </option>
            ))}
          </select>
          {isEditMode && (
            <select
              value={selectedWorkspaceId}
              onChange={(e) => setSelectedWorkspaceId(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-900/50 text-slate-900 dark:text-white text-sm"
            >
              <option value="">Select workspace *</option>
              {workspaces.map((workspace) => (
                <option
                  key={String(workspace.useCaseId)}
                  value={String(workspace.useCaseId)}
                >
                  {workspace.useCaseName}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="rounded-2xl border border-slate-300 dark:border-slate-700 p-4 bg-slate-100/50 dark:bg-transparent">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
              {isEditMode ? "Mapped User" : "Select Users"}{" "}
              <span className="text-red-500">*</span>
            </p>
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search users..."
              className="w-full sm:max-w-xs px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-900/50 text-slate-800 dark:text-slate-200 text-sm"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {filteredCompanyUsers.map((user) => {
              const userId = getUserId(user);
              const checked = selectedUsers.includes(userId);
              return (
                <label
                  key={userId}
                  className="flex items-center gap-3 p-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/30 hover:bg-slate-200/70 dark:hover:bg-slate-800/50"
                >
                  <input
                    type={isEditMode ? "radio" : "checkbox"}
                    name="mapping-user"
                    checked={checked}
                    onChange={() =>
                      setSelectedUsers(
                        isEditMode
                          ? [userId]
                          : checked
                            ? selectedUsers.filter((id) => id !== userId)
                            : [...selectedUsers, userId],
                      )
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {`${user.firstName || ""} ${user.lastName || ""}`.trim()}
                  </span>
                </label>
              );
            })}
            {filteredCompanyUsers.length === 0 && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No users found.
              </p>
            )}
          </div>
        </div>

        {!isEditMode && (
          <div className="rounded-2xl border border-slate-300 dark:border-slate-700 p-4 bg-slate-100/50 dark:bg-transparent">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                Select Workspaces <span className="text-red-500">*</span>
              </p>
              <input
                type="text"
                value={workspaceSearch}
                onChange={(e) => setWorkspaceSearch(e.target.value)}
                placeholder="Search workspaces..."
                className="w-full sm:max-w-xs px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-900/50 text-slate-800 dark:text-slate-200 text-sm"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {filteredWorkspaces.map((workspace) => {
                const workspaceId = String(workspace.useCaseId);
                const checked = selectedWorkspaceIds.includes(workspaceId);
                return (
                  <label
                    key={workspaceId}
                    className="flex items-center gap-3 p-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/30 hover:bg-slate-200/70 dark:hover:bg-slate-800/50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setSelectedWorkspaceIds((prev) =>
                          checked
                            ? prev.filter((id) => id !== workspaceId)
                            : [...prev, workspaceId],
                        )
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {workspace.useCaseName}
                    </span>
                  </label>
                );
              })}
              {filteredWorkspaces.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No workspaces found.
                </p>
              )}
            </div>
          </div>
        )}

        {!isEditMode && selectedWorkspaceIds.length > 0 && (
          <div className="space-y-5">
            {selectedWorkspaceIds.map((workspaceId) => {
              const workspace = workspaces.find(
                (item) => String(item.useCaseId) === String(workspaceId),
              );
              const workspacePayload = workspaceDetailsById[workspaceId];
              const { workspaceTableDetails, workspaceFileDetails, workspaceMediaDetails } =
                getWorkspaceDetailsBundle(workspacePayload || {});
              const selection =
                workspaceSelectionsById[workspaceId] || createEmptySelection();
              const selectedTableNamesForWorkspace =
                selection.selectedTableNames || [];
              const selectedColumnsByTableForWorkspace =
                selection.selectedColumnsByTable || {};
              const selectedFileNamesForWorkspace =
                selection.selectedFileNames || [];
              const selectedMediaNamesForWorkspace =
                selection.selectedMediaNames || [];

              return (
                <div
                  key={workspaceId}
                  className="rounded-2xl border border-slate-300 dark:border-slate-700 p-4 bg-slate-100/40 dark:bg-transparent"
                >
                  <div className="rounded-2xl border border-slate-300 dark:border-slate-700 p-4 bg-slate-100 dark:bg-slate-900/30 mb-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      <Layers size={16} className="text-amber-500" />
                      Workspace: {workspace?.useCaseName || workspaceId}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Configure subset permissions for this workspace.
                    </p>
                  </div>

                  {/* SQL Tables Section */}
                  <div className="rounded-2xl border border-slate-300 dark:border-slate-700 p-4 bg-slate-100/40 dark:bg-transparent mb-4">
                    <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
                      <Database size={16} className="text-amber-500" />
                      SQL Table Access
                    </h4>
                    {workspaceTableDetails.length === 0 && (
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        No SQL tables configured in this workspace.
                      </p>
                    )}
                    {workspaceTableDetails.length > 0 && (
                      <div className="space-y-4">
                        {workspaceTableDetails.map((tableDetail) => {
                          const tableName = tableDetail.tableOrFileName;
                          const tableChecked =
                            selectedTableNamesForWorkspace.some((selected) =>
                              isSameTableName(selected, tableName),
                            );
                          const availableColumns =
                            tableDetail.selectedFieldsList;
                          const selectedColumns =
                            selectedColumnsByTableForWorkspace[tableName] || [];
                          return (
                            <div
                              key={`${workspaceId}-${tableName}`}
                              className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 bg-slate-100 dark:bg-slate-900/30"
                            >
                              <label className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={tableChecked}
                                  onChange={() =>
                                    setWorkspaceSelectionsById((prev) => {
                                      const existing =
                                        prev[workspaceId] ||
                                        createEmptySelection();
                                      const nextTableNames =
                                        existing.selectedTableNames.some(
                                          (name) =>
                                            isSameTableName(name, tableName),
                                        )
                                          ? existing.selectedTableNames.filter(
                                              (name) =>
                                                !isSameTableName(
                                                  name,
                                                  tableName,
                                                ),
                                            )
                                          : [
                                              ...existing.selectedTableNames,
                                              tableName,
                                            ];
                                      return {
                                        ...prev,
                                        [workspaceId]: {
                                          ...existing,
                                          selectedTableNames: nextTableNames,
                                        },
                                      };
                                    })
                                  }
                                  className="w-4 h-4"
                                />
                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 font-mono">
                                  {tableName}
                                </span>
                              </label>
                              {tableChecked && (
                                <div className="mt-3 pl-7">
                                  <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                                    Allowed Columns
                                  </p>
                                  {availableColumns.length === 0 ? (
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                      No columns available for this table.
                                    </p>
                                  ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                      {availableColumns.map((columnName) => (
                                        <label
                                          key={`${workspaceId}-${tableName}-${columnName}`}
                                          className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={selectedColumns.includes(
                                              columnName,
                                            )}
                                            onChange={() =>
                                              setWorkspaceSelectionsById(
                                                (prev) => {
                                                  const existing =
                                                    prev[workspaceId] ||
                                                    createEmptySelection();
                                                  const tableColumns =
                                                    existing
                                                      .selectedColumnsByTable?.[
                                                      tableName
                                                    ] || [];
                                                  const nextColumns =
                                                    tableColumns.includes(
                                                      columnName,
                                                    )
                                                      ? tableColumns.filter(
                                                          (column) =>
                                                            column !==
                                                            columnName,
                                                        )
                                                      : [
                                                          ...tableColumns,
                                                          columnName,
                                                        ];
                                                  return {
                                                    ...prev,
                                                    [workspaceId]: {
                                                      ...existing,
                                                      selectedColumnsByTable: {
                                                        ...existing.selectedColumnsByTable,
                                                        [tableName]:
                                                          nextColumns,
                                                      },
                                                    },
                                                  };
                                                },
                                              )
                                            }
                                            className="w-4 h-4"
                                          />
                                          <span>{columnName}</span>
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* PDF Files Section */}
                  <div className="rounded-2xl border border-slate-300 dark:border-slate-700 p-4 bg-slate-100/40 dark:bg-transparent mb-4">
                    <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
                      <FileText size={16} className="text-[#36a852]" />
                      PDF Access
                    </h4>
                    {workspaceFileDetails.length === 0 && (
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        No PDFs configured in this workspace.
                      </p>
                    )}
                    {workspaceFileDetails.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {workspaceFileDetails.map((fileDetail) => (
                          <label
                            key={`${workspaceId}-${fileDetail.tableOrFileName}`}
                            className="flex items-center gap-3 p-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/30 hover:bg-slate-200/70 dark:hover:bg-slate-800/50"
                          >
                            <input
                              type="checkbox"
                              checked={selectedFileNamesForWorkspace.includes(
                                fileDetail.tableOrFileName,
                              )}
                              onChange={() =>
                                setWorkspaceSelectionsById((prev) => {
                                  const existing =
                                    prev[workspaceId] || createEmptySelection();
                                  const fileName = fileDetail.tableOrFileName;
                                  const nextFileNames =
                                    existing.selectedFileNames.includes(
                                      fileName,
                                    )
                                      ? existing.selectedFileNames.filter(
                                          (name) => name !== fileName,
                                        )
                                      : [
                                          ...existing.selectedFileNames,
                                          fileName,
                                        ];
                                  return {
                                    ...prev,
                                    [workspaceId]: {
                                      ...existing,
                                      selectedFileNames: nextFileNames,
                                    },
                                  };
                                })
                              }
                              className="w-4 h-4"
                            />
                            <FileText size={16} className="text-[#36a852] flex-shrink-0" />
                            <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                              {fileDetail.tableOrFileName}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Audio/Video Media Section - NEW */}
                  <div className="rounded-2xl border border-slate-300 dark:border-slate-700 p-4 bg-slate-100/40 dark:bg-transparent">
                    <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
                      <Video size={16} className="text-purple-500" />
                      Audio & Video Access
                    </h4>
                    {workspaceMediaDetails.length === 0 && (
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        No audio/video files configured in this workspace.
                      </p>
                    )}
                    {workspaceMediaDetails.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {workspaceMediaDetails.map((mediaDetail) => {
                          const isVideo = mediaDetail.selectedFieldsRaw?.toLowerCase().includes('video') || 
                                          mediaDetail.tableOrFileName?.toLowerCase().match(/\.(mp4|mov|avi|mkv|webm)$/);
                          return (
                            <label
                              key={`${workspaceId}-${mediaDetail.tableOrFileName}`}
                              className="flex items-center gap-3 p-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/30 hover:bg-slate-200/70 dark:hover:bg-slate-800/50"
                            >
                              <input
                                type="checkbox"
                                checked={selectedMediaNamesForWorkspace.includes(
                                  mediaDetail.tableOrFileName,
                                )}
                                onChange={() =>
                                  setWorkspaceSelectionsById((prev) => {
                                    const existing =
                                      prev[workspaceId] || createEmptySelection();
                                    const mediaName = mediaDetail.tableOrFileName;
                                    const nextMediaNames =
                                      existing.selectedMediaNames.includes(
                                        mediaName,
                                      )
                                        ? existing.selectedMediaNames.filter(
                                            (name) => name !== mediaName,
                                          )
                                        : [
                                            ...existing.selectedMediaNames,
                                            mediaName,
                                          ];
                                    return {
                                      ...prev,
                                      [workspaceId]: {
                                        ...existing,
                                        selectedMediaNames: nextMediaNames,
                                      },
                                    };
                                  })
                                }
                                className="w-4 h-4"
                              />
                              {isVideo ? (
                                <Video size={16} className="text-purple-500 flex-shrink-0" />
                              ) : (
                                <Music size={16} className="text-purple-500 flex-shrink-0" />
                              )}
                              <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                                {mediaDetail.tableOrFileName}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selectedWorkspaceData && (
          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-300 dark:border-slate-700 p-4 bg-slate-100 dark:bg-slate-900/30">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                <Layers size={16} className="text-amber-500" />
                Workspace Details Loaded
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Configure permissions from this workspace for selected users.
              </p>
            </div>

            {/* SQL Tables Section - Edit Mode */}
            <div className="rounded-2xl border border-slate-300 dark:border-slate-700 p-4 bg-slate-100/40 dark:bg-transparent">
              <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
                <Database size={16} className="text-amber-500" />
                SQL Table Access
              </h4>
              {workspaceTableDetails.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No SQL tables configured in this workspace.
                </p>
              )}
              {workspaceTableDetails.length > 0 && (
                <div className="space-y-4">
                  {workspaceTableDetails.map((tableDetail) => {
                    const tableName = tableDetail.tableOrFileName;
                    const tableChecked = selectedTableNames.some((selected) =>
                      isSameTableName(selected, tableName),
                    );
                    const availableColumns = tableDetail.selectedFieldsList;
                    const selectedColumns =
                      selectedColumnsByTable[tableName] || [];
                    return (
                      <div
                        key={tableName}
                        className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 bg-slate-100 dark:bg-slate-900/30"
                      >
                        <label className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={tableChecked}
                            onChange={() =>
                              setSelectedTableNames((prev) =>
                                prev.some((name) =>
                                  isSameTableName(name, tableName),
                                )
                                  ? prev.filter(
                                      (name) =>
                                        !isSameTableName(name, tableName),
                                    )
                                  : [...prev, tableName],
                              )
                            }
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 font-mono">
                            {tableName}
                          </span>
                        </label>
                        {tableChecked && (
                          <div className="mt-3 pl-7">
                            <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                              Allowed Columns
                            </p>
                            {availableColumns.length === 0 ? (
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                No columns available for this table.
                              </p>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {availableColumns.map((columnName) => (
                                  <label
                                    key={`${tableName}-${columnName}`}
                                    className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedColumns.includes(
                                        columnName,
                                      )}
                                      onChange={() =>
                                        setSelectedColumnsByTable((prev) => {
                                          const existing = prev[tableName] || [];
                                          const next = existing.includes(
                                            columnName,
                                          )
                                            ? existing.filter(
                                                (column) => column !== columnName,
                                              )
                                            : [...existing, columnName];
                                          return { ...prev, [tableName]: next };
                                        })
                                      }
                                      className="w-4 h-4"
                                    />
                                    <span>{columnName}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* PDF Files Section - Edit Mode */}
            <div className="rounded-2xl border border-slate-300 dark:border-slate-700 p-4 bg-slate-100/40 dark:bg-transparent">
              <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
                <FileText size={16} className="text-[#36a852]" />
                PDF Access
              </h4>
              {workspaceFileDetails.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No PDFs configured in this workspace.
                </p>
              )}
              {workspaceFileDetails.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {workspaceFileDetails.map((fileDetail) => (
                    <label
                      key={fileDetail.tableOrFileName}
                      className="flex items-center gap-3 p-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/30 hover:bg-slate-200/70 dark:hover:bg-slate-800/50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedFileNames.includes(
                          fileDetail.tableOrFileName,
                        )}
                        onChange={() =>
                          setSelectedFileNames((prev) =>
                            prev.includes(fileDetail.tableOrFileName)
                              ? prev.filter(
                                  (name) => name !== fileDetail.tableOrFileName,
                                )
                              : [...prev, fileDetail.tableOrFileName],
                          )
                        }
                        className="w-4 h-4"
                      />
                      <FileText size={16} className="text-[#36a852] flex-shrink-0" />
                      <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                        {fileDetail.tableOrFileName}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Audio/Video Media Section - Edit Mode - NEW */}
            <div className="rounded-2xl border border-slate-300 dark:border-slate-700 p-4 bg-slate-100/40 dark:bg-transparent">
              <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
                <Video size={16} className="text-purple-500" />
                Audio & Video Access
              </h4>
              {workspaceMediaDetails.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No audio/video files configured in this workspace.
                </p>
              )}
              {workspaceMediaDetails.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {workspaceMediaDetails.map((mediaDetail) => {
                    const isVideo = mediaDetail.selectedFieldsRaw?.toLowerCase().includes('video') || 
                                    mediaDetail.tableOrFileName?.toLowerCase().match(/\.(mp4|mov|avi|mkv|webm)$/);
                    return (
                      <label
                        key={mediaDetail.tableOrFileName}
                        className="flex items-center gap-3 p-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/30 hover:bg-slate-200/70 dark:hover:bg-slate-800/50"
                      >
                        <input
                          type="checkbox"
                          checked={selectedMediaNames.includes(
                            mediaDetail.tableOrFileName,
                          )}
                          onChange={() =>
                            setSelectedMediaNames((prev) =>
                              prev.includes(mediaDetail.tableOrFileName)
                                ? prev.filter(
                                    (name) => name !== mediaDetail.tableOrFileName,
                                  )
                                : [...prev, mediaDetail.tableOrFileName],
                            )
                          }
                          className="w-4 h-4"
                        />
                        {isVideo ? (
                          <Video size={16} className="text-purple-500 flex-shrink-0" />
                        ) : (
                          <Music size={16} className="text-purple-500 flex-shrink-0" />
                        )}
                        <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                          {mediaDetail.tableOrFileName}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 rounded-xl">
            {error}
          </div>
        )}
      </div>

      <div className="p-5 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
        <button
          onClick={onCancel}
          className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white px-6 py-2.5 rounded-full font-medium transition-colors"
        >
          Back to Mappings
        </button>
        <button
          onClick={handleSave}
          disabled={
            isSaving ||
            !selectedCompanyId ||
            selectedUsers.length === 0 ||
            (isEditMode
              ? !selectedWorkspaceId
              : selectedWorkspaceIds.length === 0)
          }
          className="bg-[#1d57a5] hover:bg-[#164280] disabled:bg-slate-300 disabled:text-slate-500 text-white px-6 py-2.5 rounded-full font-semibold transition-all flex items-center gap-2"
        >
          {isSaving ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckSquare size={16} />
              {isEditMode ? "Update Mapping" : "Create Mapping"}
            </>
          )}
        </button>
      </div>
      {/* Toast */}
      {toast.visible && (
        <div className="fixed top-6 right-6 z-[9999] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${toast.type === "success" ? "bg-[#36a852]" : "bg-red-500"}`}
          >
            {toast.type === "success" ? (
              <Check size={14} strokeWidth={3} />
            ) : (
              <span
                style={{ fontSize: "14px", fontWeight: "bold", lineHeight: 1 }}
              >
                ✕
              </span>
            )}
          </div>
          <span className="text-sm font-semibold">{toast.message}</span>
        </div>
      )}
    </div>
  );
}